import FilePickerAction from '../../store/schema/FilePickerAction';
import { format } from 'owa-strings';
import { getExtensionFromFileName } from 'owa-file';
import FileProviderItemViewState, {
    FileProviderItemType,
} from '../../store/schema/FileProviderItemViewState';
import FilePickerActionMessageType, {
    actionMessageMap,
} from '../../types/FilePickerActionMessageType';

interface UnsupportedExtensionOrMimes {
    extensionOrMimes: string[];
    message: string;
}

const unsupportedAttachForAllProviders: UnsupportedExtensionOrMimes[] = [
    {
        extensionOrMimes: [
            'application/vnd.google-apps.document',
            'application/vnd.google-apps.spreadsheet',
            'application/vnd.google-apps.presentation',
            'application/vnd.google-apps.drawing',
            'application/vnd.google-apps.file',
            'application/vnd.google-apps.form',
            'application/vnd.google-apps.fusiontable',
            'application/vnd.google-apps.map',
            'application/vnd.google-apps.script',
            'application/vnd.google-apps.sites',
        ],
        message: actionMessageMap[FilePickerActionMessageType.AttachGoogleFiles](),
    },
    {
        extensionOrMimes: ['application/onenote'],
        message: actionMessageMap[FilePickerActionMessageType.AttachOneNote](),
    },
];

export interface FileProviderItemsSupportedActions {
    actions: FilePickerAction[];
    messages: string[];
}

function deleteFromArray(array: any[], item: any) {
    const index = array.indexOf(item);

    if (index !== -1) {
        array.splice(index, 1);
    }
}

function itemHasExtensionOrMime(extensionOrMimeList: string[], includeMime: boolean = true) {
    return (item: FileProviderItemViewState) => {
        const extension = (getExtensionFromFileName(item.model.Name) || '').toLowerCase();
        const mimeType = (item.model.MimeType || '').toLowerCase();

        return extensionOrMimeList.some(
            extensionOrMime =>
                extensionOrMime === extension || (includeMime && extensionOrMime === mimeType)
        );
    };
}

export function createFileProviderItemsSupportedActionsFunc({
    fileExtensionOrMimeSupportedForInsert = [],
}: {
    fileExtensionOrMimeSupportedForInsert?: string[];
}): (
    items: FileProviderItemViewState[],
    isSharingFolderSupported: boolean,
    providerDisplayName: string
) => FileProviderItemsSupportedActions {
    return function getItemsSupportedActions(
        items: FileProviderItemViewState[],
        isSharingFolderSupported: boolean,
        providerDisplayName: string
    ) {
        const actions = [FilePickerAction.Attach, FilePickerAction.Share];
        const messages = [];

        if (items.some(item => item.type === FileProviderItemType.Folder)) {
            deleteFromArray(actions, FilePickerAction.Attach);
            messages.push(
                format(
                    actionMessageMap[FilePickerActionMessageType.AttachFolder](),
                    providerDisplayName
                )
            );

            if (!isSharingFolderSupported) {
                deleteFromArray(actions, FilePickerAction.Share);
                // No error message for this as of now. TODO: Ask Rebecca is we should have an error message here, and
                // change the error message above in this situation (since it won't make sense).
            }
        }

        unsupportedAttachForAllProviders.forEach(unsupported => {
            if (items.some(itemHasExtensionOrMime(unsupported.extensionOrMimes))) {
                deleteFromArray(actions, FilePickerAction.Attach);
                messages.push(unsupported.message);
            }
        });

        if (messages === [] && items.some(itemHasExtensionOrMime([''], false))) {
            deleteFromArray(actions, FilePickerAction.Attach);
            messages.push(
                format(
                    actionMessageMap[FilePickerActionMessageType.AttachWithoutExtension](),
                    providerDisplayName
                )
            );
        }

        if (items.every(itemHasExtensionOrMime(fileExtensionOrMimeSupportedForInsert))) {
            actions.push(FilePickerAction.Insert);
        }

        return {
            actions: actions,
            messages: messages,
        };
    };
}

// Provider specific function
export function mailboxProviderGetSupportedActions(
    items: FileProviderItemViewState[],
    isSharingFolderSupported: boolean
): FileProviderItemsSupportedActions {
    const actions = [FilePickerAction.Attach, FilePickerAction.Share];

    if (items.some(item => item.type === FileProviderItemType.Folder)) {
        // For folders the attach is not supported at all
        deleteFromArray(actions, FilePickerAction.Attach);

        if (!isSharingFolderSupported) {
            deleteFromArray(actions, FilePickerAction.Share);
        }
    }

    return {
        actions: actions,
        messages: [],
    };
}
